import { useEffect, useState } from 'react'
import { Zap } from 'lucide-react'
import { serviceApi } from '../../services/api'
import { emotionZone } from '../../utils/emotionZone'
import { Avatar } from '../patterns/Avatar'
import StressPairDrawer from './StressPairDrawer'

interface Actor {
  name: string
  avatarUrl: string | null
  deptName: string | null
  position: string | null
}

interface StressPair {
  aRef: string
  bRef: string
  pairIndex: number
  negativeCount: number
  sharedConvs: number
  sampleSize: number
  a: Actor
  b: Actor
}

const PAIR_CAP = 8

export default function StressConnections({ kind }: { kind: 'employee' | 'client' | 'partner' }) {
  const [pairs, setPairs] = useState<StressPair[] | null>(null)
  const [openPair, setOpenPair] = useState<StressPair | null>(null)

  useEffect(() => {
    setPairs(null)
    serviceApi
      .interactionStress(`window=30&kind=${kind}`)
      .then((d: { pairs: StressPair[] }) => setPairs(d.pairs))
      .catch(() => setPairs([]))
  }, [kind])

  const zone = (index: number) => emotionZone(index)

  return (
    <section className="rounded-lg ring-1 ring-border bg-card p-4">
      <h2
        className="flex items-center gap-2 text-sm font-medium text-foreground mb-3"
        title="Pairs are ranked by negative interaction weight across calls, channels, and DMs. Many-party conversations are approximate."
      >
        <Zap size={16} className="text-destructive" />
        Most stressful connections
      </h2>
      <div className="flex flex-col gap-1">
        {pairs === null && (
          <p className="py-2 text-sm text-muted-foreground">Loading…</p>
        )}
        {pairs !== null && pairs.length === 0 && (
          <p className="py-2 text-sm text-muted-foreground">No stressful pairs in range.</p>
        )}
        {pairs !== null &&
          pairs.slice(0, PAIR_CAP).map((pair) => {
            const z = zone(pair.pairIndex)
            const pillBg =
              z.text === 'text-destructive'
                ? 'bg-destructive/15'
                : z.text === 'text-amber-500'
                  ? 'bg-amber-500/15'
                  : 'bg-emerald-500/15'
            return (
              <button
                type="button"
                onClick={() => setOpenPair(pair)}
                key={`${pair.aRef}__${pair.bRef}`}
                className="flex items-center gap-3 px-2 py-1.5 rounded-md border border-border bg-card w-full text-left cursor-pointer hover:bg-muted transition duration-150"
              >
                {/* Overlapping avatars */}
                <div className="flex items-center shrink-0">
                  <Avatar name={pair.a.name} src={pair.a.avatarUrl} className="h-6 w-6 text-[10px] z-10 relative" />
                  <Avatar name={pair.b.name} src={pair.b.avatarUrl} className="h-6 w-6 text-[10px] -ml-3 ring-2 ring-card" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate">
                    {pair.a.name} ↔ {pair.b.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {pair.negativeCount} negative · {pair.sharedConvs} shared
                  </p>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium tabular-nums ${z.text} ${pillBg}`}>
                  {pair.pairIndex}
                </span>
              </button>
            )
          })}
      </div>
      {openPair && (
        <StressPairDrawer
          pair={{ aRef: openPair.aRef, bRef: openPair.bRef, aName: openPair.a.name, bName: openPair.b.name }}
          kind={kind}
          onClose={() => setOpenPair(null)}
        />
      )}
    </section>
  )
}
